import React from 'react';
import { createPortal } from 'react-dom';
import Logo from '../assets/logo_kd.png';
import { IconPrinter, IconX } from '@tabler/icons-react';

const SuratJalanPrint = ({ isOpen, closeModal, data, breakdown = [], categoryKaratBreakdown = [] }) => {
  if (!isOpen || !data) return null;

  const handlePrint = () => {
    window.print();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black/60 backdrop-blur-sm flex flex-col items-center p-2 sm:p-6">
      
      {/* Modal Actions Header (Hidden when printing) */}
      <div className="w-full max-w-4xl flex justify-end items-center gap-3 mb-4 mt-4 sm:mt-0 print:hidden">
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold shadow-lg transition-all"
        >
          <IconPrinter size={18} />
          <span>Cetak Surat Jalan</span>
        </button>
        <button
          onClick={closeModal}
          className="p-2 bg-slate-800/80 hover:bg-slate-900 text-white rounded-lg shadow-lg transition-all"
        >
          <IconX size={20} />
        </button>
      </div>

      {/* A4 Printable Document Paper Container */}
      <div className="bg-white text-slate-900 w-full max-w-4xl p-6 sm:p-10 rounded-xl shadow-2xl mb-8 print:my-0 print:p-0 print:shadow-none print:w-full print:max-w-none print:rounded-none">
        
        {/* Document Header */}
        <div className="flex items-center justify-between border-b-2 border-amber-500 pb-4 mb-6">
          <div className="flex items-center gap-4">
            <img src={Logo} alt="Komala Dewi" className="w-16 h-16 object-contain" />
            <div>
              <h1 className="text-2xl font-extrabold uppercase tracking-wider text-amber-700">TOKO EMAS KOMALA DEWI</h1>
              <p className="text-xs text-slate-600 font-medium">Jual Beli Perhiasan Emas & Terima Pesanan</p>
              <p className="text-[11px] text-slate-500">Dokumen Pengiriman Barang (Delivery Order / Surat Jalan)</p>
            </div>
          </div>
          <div className="text-right">
            <div className="inline-block px-3 py-1 bg-amber-100 border border-amber-300 rounded-md text-amber-800 font-bold text-sm mb-1 uppercase tracking-wider">
              SURAT JALAN
            </div>
            <p className="text-xs font-mono font-bold text-slate-800">{data.no_surat_jalan}</p>
            <p className="text-[11px] text-slate-500">{data.tanggal ? new Date(data.tanggal).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-4 bg-amber-50/50 p-4 rounded-lg border border-amber-200/60 mb-6 text-xs">
          <div>
            <p className="text-slate-500 font-semibold mb-1">PENGIRIM (ASAL):</p>
            <p className="font-bold text-sm text-slate-800">{data.cabang_asal?.nama_cabang || 'Brankas Pusat (Rumah)'}</p>
            <p className="text-slate-600">Lokasi: {data.cabang_asal?.lokasi || '-'}</p>
            <p className="text-slate-600">Pemberi Barang: <span className="font-semibold">{data.pemberi_user?.name || '-'}</span></p>
          </div>
          <div>
            <p className="text-slate-500 font-semibold mb-1">PENERIMA (TUJUAN):</p>
            <p className="font-bold text-sm text-slate-800">{data.cabang_tujuan?.nama_cabang || '-'}</p>
            <p className="text-slate-600">Kurir / Pengantar: <span className="font-semibold text-amber-700">{data.nama_kurir}</span></p>
            <p className="text-slate-600">Validator Toko: <span className="font-semibold">{data.penerima_user?.name || '(Menunggu Validasi Toko)'}</span></p>
          </div>
        </div>

        {/* Category & Karat Summary */}
        {categoryKaratBreakdown && categoryKaratBreakdown.length > 0 && (
          <div className="space-y-3 bg-amber-50/50 p-4 rounded-md border border-amber-100 mb-6 print:bg-white print:border-slate-300">
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-amber-800 print:text-slate-700">
              Ringkasan Item Berdasarkan Kategori & Karat
            </h4>
            <div className="space-y-3">
              {categoryKaratBreakdown.map((cat, idx) => (
                <div key={idx} className="bg-white p-3 rounded-lg border border-slate-100 shadow-sm print:border-slate-200 print:shadow-none">
                  <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-50 print:border-slate-200">
                    <span className="font-bold text-slate-700 text-sm">{cat.name}</span>
                    <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-md print:bg-slate-100 print:text-slate-700 print:border print:border-slate-300">
                      {cat.totalPcs} Pcs ({cat.totalBerat.toFixed(3)}g)
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(cat.karats).map(([karat, dataVal]) => (
                      <div key={karat} className="flex items-center gap-2 bg-slate-50 px-2 py-1 rounded text-xs border border-slate-100 print:bg-white print:border-slate-200">
                        <span className="text-slate-500 font-medium">{karat} Karat</span>
                        <span className="font-bold text-slate-700">
                          {dataVal.pcs} Pcs / {dataVal.berat.toFixed(3)}g
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            {/* TOTAL SEMUANYA */}
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-amber-200 print:border-slate-300">
              <span className="font-bold text-sm text-slate-800 uppercase tracking-wide">Total Keseluruhan</span>
              <span className="font-bold text-sm bg-amber-500 text-white px-3 py-1.5 rounded-lg shadow-sm print:bg-white print:text-slate-800 print:border-2 print:border-slate-800">
                {data.total_pcs} Pcs / {parseFloat(data.total_berat).toFixed(3)} gr
              </span>
            </div>
          </div>
        )}



        {/* Catatan / Alasan Penolakan Jika Ada */}
        {data.catatan_pengiriman && (
          <div className="mb-4 text-xs bg-slate-50 p-2.5 rounded border border-slate-200">
            <span className="font-bold text-slate-700">Catatan Pengirim:</span> {data.catatan_pengiriman}
          </div>
        )}
        {data.catatan_penerimaan && (
          <div className="mb-4 text-xs bg-rose-50 text-rose-800 p-2.5 rounded border border-rose-200">
            <span className="font-bold">Catatan Penerima/Validasi:</span> {data.catatan_penerimaan}
          </div>
        )}

        {/* Signature Blocks (4 Pihak) */}
        <div className="mt-8 pt-4 border-t border-slate-300 grid grid-cols-4 gap-2 text-center text-xs">
          <div>
            <p className="font-semibold text-slate-600 mb-12">Pemberi Barang (Rumah)</p>
            <p className="font-bold border-t border-slate-400 pt-1 text-slate-800">{data.pemberi_user?.name || '(Owner / Rumah)'}</p>
          </div>
          <div>
            <p className="font-semibold text-slate-600 mb-12">Kurir / Pengantar</p>
            <p className="font-bold border-t border-slate-400 pt-1 text-slate-800">{data.nama_kurir}</p>
          </div>
          <div>
            <p className="font-semibold text-slate-600 mb-12">Penerima (Validator Toko)</p>
            <p className="font-bold border-t border-slate-400 pt-1 text-slate-800">{data.penerima_user?.name || '(Ttd & Stempel Toko)'}</p>
          </div>
          <div>
            <p className="font-semibold text-slate-600 mb-12">Mengetahui (Kepala Toko)</p>
            <p className="font-bold border-t border-slate-400 pt-1 text-slate-800">(Ttd & Stempel)</p>
          </div>
        </div>

        {/* Print Footer */}
        <div className="mt-6 text-[10px] text-slate-400 text-center uppercase tracking-widest border-t border-slate-200 pt-2">
          Komala Dewi System V5 • Cetakan Otomatis Dokumen Serah Terima Pengiriman
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SuratJalanPrint;
